/* eslint-disable max-len */
import * as React from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/util/cn';

// Shared table primitive. Replaces common/Grid (column-config) and the
// bespoke <table> usages across the codebase with a single JSX-composition
// abstraction.
//
//   • Header cells: 11px / semibold / uppercase / tracking-wide /
//     secondary-blue at 80% opacity (no per-page styling drift).
//   • Body cells: 14px / medium / tabular-nums / foreground (or muted).
//   • Body rows: opt-in zebra background via the `zebra` prop.
//   • Wrapper handles overflow-x-auto + rounded border + min-width.
//
// Sort is opt-in. The page owns sorted data — DataTable just renders the
// caret on the active header cell and emits `onSortChange`. No generic
// types, no column-config boilerplate, no internal data manipulation.
//
// Without sort:
//
//   <DataTable minWidth={720}>
//     <DataTable.Head>
//       <DataTable.HeadRow>
//         <DataTable.HeadCell>Resort</DataTable.HeadCell>
//         <DataTable.HeadCell align="right">Base depth</DataTable.HeadCell>
//       </DataTable.HeadRow>
//     </DataTable.Head>
//     <DataTable.Body>
//       {rows.map((r, i) => (
//         <DataTable.Row key={r.name} zebra={i % 2 === 0}>
//           <DataTable.RowHeader>{r.name}</DataTable.RowHeader>
//           <DataTable.Cell align="right">{r.baseDepth}</DataTable.Cell>
//         </DataTable.Row>
//       ))}
//     </DataTable.Body>
//   </DataTable>
//
// With sort — opt-in via two props on the root + sortKey on cells:
//
//   const [sort, setSort] = useState<DataTableSort | null>({
//     key: 'baseDepth', dir: 'desc',
//   });
//   const sortedRows = useMemo(() => sortRows(rows, sort), [rows, sort]);
//
//   <DataTable sort={sort} onSortChange={setSort}>
//     <DataTable.Head>
//       <DataTable.HeadRow>
//         <DataTable.HeadCell sortKey="name">Resort</DataTable.HeadCell>
//         <DataTable.HeadCell align="right" sortKey="baseDepth" defaultDir="desc">
//           Base depth
//         </DataTable.HeadCell>
//       </DataTable.HeadRow>
//     </DataTable.Head>
//     <DataTable.Body>
//       {sortedRows.map(/* page is responsible for sorting */)}
//     </DataTable.Body>
//   </DataTable>

export type SortDirection = 'asc' | 'desc';

export type DataTableSort = {
  key: string;
  dir: SortDirection;
};

type DataTableContextValue = {
  sort: DataTableSort | null;
  onSortChange?: (next: DataTableSort) => void;
};

const DataTableContext = React.createContext<DataTableContextValue>({ sort: null });

type Align = 'left' | 'right' | 'center';

const alignClass = (a: Align = 'left') => ({
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
}[a]);

const alignFlex = (a: Align = 'left') => ({
  left: 'justify-start',
  right: 'justify-end',
  center: 'justify-center',
}[a]);

// ─── Root ────────────────────────────────────────────────────────────────

type RootProps = {
  /** Min table width before horizontal scroll kicks in. */
  minWidth?: number;
  /** Active sort state. Set to enable sort on the table; pass `null` for unsorted. */
  sort?: DataTableSort | null;
  /** Fired when the user clicks a sortable header. The page receives the new sort and is responsible for re-sorting the data. */
  onSortChange?: (next: DataTableSort) => void;
  className?: string;
  children: React.ReactNode;
};

const Root = ({
  minWidth = 560, sort = null, onSortChange, className, children,
}: RootProps) => {
  const ctx = React.useMemo<DataTableContextValue>(
    () => ({ sort, onSortChange }),
    [sort, onSortChange],
  );
  return (
    <DataTableContext.Provider value={ctx}>
      <div className={cn('overflow-x-auto rounded-md border border-border', className)}>
        <table className="w-full text-sm" style={{ minWidth }}>
          {children}
        </table>
      </div>
    </DataTableContext.Provider>
  );
};

// ─── Sections ────────────────────────────────────────────────────────────

const Head = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <thead className={className}>{children}</thead>
);

const Body = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <tbody className={className}>{children}</tbody>
);

const HeadRow = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <tr className={cn('bg-muted/40', className)}>{children}</tr>
);

type RowProps = {
  zebra?: boolean;
  className?: string;
  children: React.ReactNode;
};

const Row = ({ zebra, className, children }: RowProps) => (
  <tr className={cn(zebra ? 'bg-sky-50/50' : 'bg-background', className)}>
    {children}
  </tr>
);

// ─── HeadCell ────────────────────────────────────────────────────────────

type HeadCellProps = {
  align?: Align;
  /** When set, the cell is interactive and clicking it emits `onSortChange` with this key. */
  sortKey?: string;
  /** Direction emitted on the first click of an inactive sortable header. Numeric columns typically want `'desc'` (biggest first). Defaults to `'asc'`. */
  defaultDir?: SortDirection;
  className?: string;
  /** Optional — empty header cells above row-label columns are common. */
  children?: React.ReactNode;
};

const SortCaret = ({ active, dir }: { active: boolean; dir: SortDirection | null }) => {
  if (!active) {
    return <ChevronsUpDown size={12} className="fill-none opacity-40" aria-hidden />;
  }
  return dir === 'asc'
    ? <ChevronUp size={12} className="fill-none" aria-hidden />
    : <ChevronDown size={12} className="fill-none" aria-hidden />;
};

const HeadCell = ({
  align, sortKey, defaultDir = 'asc', className, children,
}: HeadCellProps) => {
  const { sort, onSortChange } = React.useContext(DataTableContext);
  const isSortable = !!sortKey && !!onSortChange;
  const isActive = isSortable && sort?.key === sortKey;

  const handleClick = () => {
    if (!isSortable || !onSortChange || !sortKey) return;
    if (isActive && sort) {
      onSortChange({ key: sortKey, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
    } else {
      onSortChange({ key: sortKey, dir: defaultDir });
    }
  };

  let ariaSort: 'ascending' | 'descending' | 'none' | undefined;
  if (isActive) {
    ariaSort = sort?.dir === 'asc' ? 'ascending' : 'descending';
  } else if (isSortable) {
    ariaSort = 'none';
  }

  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={cn(
        'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-secondary/80',
        alignClass(align),
        isSortable && 'cursor-pointer select-none transition hover:text-secondary',
        className,
      )}
      onClick={isSortable ? handleClick : undefined}
    >
      {isSortable ? (
        <span className={cn('inline-flex items-center gap-1.5', alignFlex(align))}>
          {children}
          <SortCaret active={!!isActive} dir={isActive ? sort!.dir : null} />
        </span>
      ) : children}
    </th>
  );
};

// ─── Body cells ──────────────────────────────────────────────────────────

type RowHeaderProps = {
  align?: Align;
  className?: string;
  children: React.ReactNode;
};

const RowHeader = ({ align, className, children }: RowHeaderProps) => (
  <th
    scope="row"
    className={cn(
      'px-4 py-3.5 font-semibold text-foreground',
      alignClass(align),
      className,
    )}
  >
    {children}
  </th>
);

type CellProps = {
  align?: Align;
  muted?: boolean;
  className?: string;
  children: React.ReactNode;
};

const Cell = ({
  align, muted, className, children,
}: CellProps) => (
  <td
    className={cn(
      'px-4 py-3.5 font-medium tabular-nums',
      muted ? 'text-muted-foreground' : 'text-foreground',
      alignClass(align),
      className,
    )}
  >
    {children}
  </td>
);

// ─── Namespace export ────────────────────────────────────────────────────

export const DataTable = Object.assign(Root, {
  Head,
  HeadRow,
  HeadCell,
  Body,
  Row,
  RowHeader,
  Cell,
});
